import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { Option, AircraftOverride, MarketEMP } from '../types/database'

interface ABBData {
  year_model: number
  year: number
  quarter: number
  value: number
}

// Breakdown data for relative value display
export interface RelativeValueBreakdown {
  total: number | null
  abbValue: number | null  // "ABB Base Value"
  optionsTotal: number     // Options adjustment
  maintenanceAdjustment: number  // AFTT adjustment
  empCostAdjustment: number  // EMP adjustment
}

interface MarketSettingsData {
  fleet_avg_aftt: number | null
  maintenance_cost: number | null
  use_manufacturing_date: boolean
}

// Cache for ABB values, market settings, overrides, and EMPs to prevent redundant fetches
const abbCache = new Map<string, ABBData[]>()
const marketSettingsCache = new Map<number, MarketSettingsData>()
const overridesCache = new Map<string, Map<number, AircraftOverride>>()
const empsCache = new Map<number, MarketEMP | null>()

export function useRelativeValue(
  modelId: number,
  aircraftData: Array<{
    aircraft_id: number
    yeardlv: number | null
    yearmfr: number | null
    aftt: number | null
    transDate?: string | null // For sold aircraft
  }>,
  optionsMap: Map<number, Option[]>
) {
  const [values, setValues] = useState<Map<number, number | null>>(new Map())
  const [breakdowns, setBreakdowns] = useState<Map<number, RelativeValueBreakdown>>(new Map())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [missingABB, setMissingABB] = useState<Set<number>>(new Set())
  const [dataVersion, setDataVersion] = useState(0)

  useEffect(() => {
    if (!modelId || aircraftData.length === 0) {
      setValues(new Map())
      setBreakdowns(new Map())
      return
    }

    const fetchRelativeValues = async () => {
      setLoading(true)
      setError(null)

      try {
        // Check cache for market settings
        let marketSettings: MarketSettingsData | null = marketSettingsCache.get(modelId) || null
        
        if (!marketSettings) {
          // Fetch market settings
          const { data, error: settingsError } = await supabase
            .from('market_settings')
            .select('fleet_avg_aftt, maintenance_cost, use_manufacturing_date')
            .eq('model_id', modelId)
            .maybeSingle()

          if (settingsError && settingsError.code !== 'PGRST116') {
            throw settingsError
          }

          marketSettings = data ? {
            ...data,
            use_manufacturing_date: data.use_manufacturing_date ?? false
          } : null
          if (marketSettings) {
            marketSettingsCache.set(modelId, marketSettings)
          }
        }

        // If no market settings, we can't calculate relative value
        if (!marketSettings || marketSettings.fleet_avg_aftt === null || marketSettings.maintenance_cost === null) {
          const emptyMap = new Map<number, number | null>()
          aircraftData.forEach(aircraft => {
            emptyMap.set(aircraft.aircraft_id, null)
          })
          setValues(emptyMap)
          return
        }

        // Determine which year field to use based on market settings
        const useManufacturingDate = marketSettings.use_manufacturing_date

        // Get all unique year models (using the appropriate date field)
        const yearModels = [...new Set(aircraftData.map(a =>
          useManufacturingDate ? a.yearmfr : a.yeardlv
        ).filter(y => y !== null))] as number[]

        if (yearModels.length === 0) {
          const emptyMap = new Map<number, number | null>()
          aircraftData.forEach(aircraft => {
            emptyMap.set(aircraft.aircraft_id, null)
          })
          setValues(emptyMap)
          return
        }

        // Check cache for ABB values
        const abbCacheKey = `${modelId}-${yearModels.sort().join(',')}`
        let abbData = abbCache.get(abbCacheKey)
        
        if (!abbData) {
          // Fetch ABB values for all year models
          const { data, error: abbError } = await supabase
            .from('abb')
            .select('year_model, year, quarter, value')
            .eq('model_id', modelId)
            .in('year_model', yearModels)
            .order('year', { ascending: false })
            .order('quarter', { ascending: false })

          if (abbError) throw abbError
          
          abbData = data
          if (abbData) {
            abbCache.set(abbCacheKey, abbData)
          }
        }

        // Fetch aircraft overrides to check EMP status
        const aircraftIds = aircraftData.map(a => a.aircraft_id)
        const overridesCacheKey = aircraftIds.sort().join(',')
        let overridesMap = overridesCache.get(overridesCacheKey)

        if (!overridesMap) {
          overridesMap = new Map<number, AircraftOverride>()
          if (aircraftIds.length > 0) {
            const { data: overridesData, error: overridesError } = await supabase
              .from('ac_overrides')
              .select('*')
              .in('aircraft_id', aircraftIds)

            if (overridesError) {
              // Error fetching overrides
            } else if (overridesData) {
              overridesData.forEach(override => {
                overridesMap!.set(override.aircraft_id, override)
              })
            }
          }
          overridesCache.set(overridesCacheKey, overridesMap)
        }

        // Fetch active EMP data for this model (there should only be one active)
        let activeEmpData: MarketEMP | null = empsCache.get(modelId) as MarketEMP | null
        if (activeEmpData === undefined) {
          const { data, error: empsError } = await supabase
            .from('market_emps')
            .select('*')
            .eq('model_id', modelId)
            .eq('is_active', true)
            .single()

          if (empsError) {
            // If no active EMP found, that's okay - set to null
            if (empsError.code === 'PGRST116') {
              activeEmpData = null
            } else {
              // Error fetching active EMP
              activeEmpData = null
            }
          } else {
            activeEmpData = data
          }
          empsCache.set(modelId, activeEmpData)
        }

        // Calculate relative values for each aircraft
        const currentYear = new Date().getFullYear()
        const resultMap = new Map<number, number | null>()
        const breakdownsMap = new Map<number, RelativeValueBreakdown>()
        const missingABBSet = new Set<number>()

        aircraftData.forEach(aircraft => {
          try {
            // Determine the year to use based on market settings
            const yearForABB = useManufacturingDate ? aircraft.yearmfr : aircraft.yeardlv

            // If no year or aftt, can't calculate
            if (!yearForABB || !aircraft.aftt) {
              resultMap.set(aircraft.aircraft_id, null)
              return
            }

            // Get ABB value based on transaction date (for sold) or most recent (for others)
            let abbValue: number | null = null

            // Get all ABB entries for this year_model (already sorted by year/quarter desc)
            const abbMatchesForYearModel = abbData?.filter((d: ABBData) => d.year_model === yearForABB) || []

            if (aircraft.transDate) {
              // For sold aircraft, try to use the quarter of the sale
              const transDate = new Date(aircraft.transDate)
              const transYear = transDate.getFullYear()
              const transQuarter = Math.floor(transDate.getMonth() / 3) + 1

              // First try exact match for the transaction quarter
              const exactMatch = abbMatchesForYearModel.find(
                (d: ABBData) => d.year === transYear && d.quarter === transQuarter
              )

              if (exactMatch) {
                abbValue = exactMatch.value
              } else if (abbMatchesForYearModel.length > 0) {
                // Fallback: Find the closest ABB quarter to the transaction date
                // Convert each ABB entry to a comparable date value (year * 4 + quarter)
                const transDateValue = transYear * 4 + transQuarter

                // Sort by distance from transaction date
                const sortedByDistance = [...abbMatchesForYearModel].sort((a, b) => {
                  const distA = Math.abs((a.year * 4 + a.quarter) - transDateValue)
                  const distB = Math.abs((b.year * 4 + b.quarter) - transDateValue)
                  return distA - distB
                })

                abbValue = sortedByDistance[0].value
              }
            } else {
              // For current aircraft, use most recent ABB value
              if (abbMatchesForYearModel.length > 0) {
                abbValue = abbMatchesForYearModel[0].value // Already sorted by year/quarter desc
              }
            }

            if (!abbValue) {
              resultMap.set(aircraft.aircraft_id, null)
              missingABBSet.add(aircraft.aircraft_id)
              return
            }

            // Skip if market settings are not available
            if (!marketSettings || marketSettings.fleet_avg_aftt === null || marketSettings.maintenance_cost === null) {
              resultMap.set(aircraft.aircraft_id, null)
              return
            }

            // Calculate maintenance adjustment (use same year as ABB lookup)
            const expectedAFTT = marketSettings.fleet_avg_aftt * (currentYear - yearForABB)
            const afttDifference = expectedAFTT - aircraft.aftt
            const maintenanceAdjustment = afttDifference * marketSettings.maintenance_cost

            // Calculate options total
            const options = optionsMap.get(aircraft.aircraft_id) || []
            const optionsTotal = options.reduce((sum, option) => sum + option.value, 0)

            // Calculate EMP cost adjustment
            let empCostAdjustment = 0
            const override = overridesMap?.get(aircraft.aircraft_id)
            const aircraftEMP = override?.emp

            // If aircraft is NOT on an EMP, apply EMP cost per hour calculation using the active EMP
            if (!aircraftEMP || aircraftEMP.trim() === '') {
              // Use the active EMP for this model (there should only be one)
              if (activeEmpData && activeEmpData.cost_per_hour !== null && activeEmpData.cost_per_hour !== undefined && activeEmpData.cost_per_hour > 0) {
                // Multiply aircraft flight hours (aftt) by active EMP cost per hour
                empCostAdjustment = aircraft.aftt * activeEmpData.cost_per_hour
              }
            }

            // Calculate relative value
            const relativeValue = abbValue + maintenanceAdjustment + optionsTotal - empCostAdjustment
            resultMap.set(aircraft.aircraft_id, Math.round(relativeValue))

            // Store breakdown data
            breakdownsMap.set(aircraft.aircraft_id, {
              total: Math.round(relativeValue),
              abbValue,
              optionsTotal,
              maintenanceAdjustment: Math.round(maintenanceAdjustment),
              empCostAdjustment: Math.round(empCostAdjustment)
            })
          } catch (err) {
            resultMap.set(aircraft.aircraft_id, null)
          }
        })

        setValues(resultMap)
        setBreakdowns(breakdownsMap)
        setMissingABB(missingABBSet)
        setDataVersion(v => v + 1) // Increment version to trigger memoization update
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to calculate relative values')
      } finally {
        setLoading(false)
      }
    }

    fetchRelativeValues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, aircraftData.map(a => `${a.aircraft_id}-${a.yeardlv}-${a.yearmfr}-${a.aftt}-${a.transDate || ''}`).join(','), optionsMap.size, Array.from(optionsMap.keys()).join(',')])

  // Memoize the data to prevent unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedValues = useMemo(() => values, [dataVersion])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedBreakdowns = useMemo(() => breakdowns, [dataVersion])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedMissingABB = useMemo(() => missingABB, [dataVersion])

  return { values: memoizedValues, breakdowns: memoizedBreakdowns, loading, error, missingABB: memoizedMissingABB }
}